import { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Stack,
  Typography,
  DialogContent,
  FormControlLabel,
  styled,
  Switch,
  SwitchProps,
  Box,
  InputBase,
} from "@mui/material";
import { usePrinter, useUpdateAction } from "../../hooks";
import { globalTheme } from "../../styles";
import { useCreateShipmentRequest } from "../../hooks/useCreateShipmentRequest";
import { ICreateShipmentRequest } from "../../interfaces/ICreateShipmentRequest";
import { UPDATE_TRACKING_ALLOWED_IDS } from "../../constants";

export const PrintByItemValidationModalContent = ({
  validatingItems,
  onValidation,
  onError,
  onClose,
  manualShipmentsData,
  firstItemBarcode,
}) => {
  const [validationSearchText, setValidationSearchText] = useState<string>(firstItemBarcode);
  const [enableCreateShipmentApiCall, setEnableCreateShipmentApiCall] = useState<boolean>(false);
  const [enablePrintProcedure, setEnablePrintProcedure] = useState<boolean>(false);
  const [newLabelNumber, setNewLabelNumber] = useState<string>("");
  const [isCustomTrackingAllowed, setIsCustomTrackingAllowed] = useState<boolean>(false);
  const [labelNumberFLag, setLabelNumberFLag] = useState<boolean>(false);
  const [validatingItemsData, setValidatingItemsData] = useState(
    validatingItems.items.map((item, id) => {
      return {
        ...item,
        validatedQuantity: 0,
        isFullyValidated: false,
        shipmentDetails: validatingItems?.shipmentDetails,
      };
    })
  );
  const [allItemsValidated, setAllItemsValidated] = useState(false);

  const updatedShipmentRequest: ICreateShipmentRequest = {
    ...validatingItems?.selectedQuote,
    shipmentItems:
      validatingItems?.selectedQuote?.shipmentItems?.map((item) => {
        return {
          ...item,
          labelNumber: newLabelNumber,
        };
      }) || [],
  };

  const { createShipmentRequestResponse } = useCreateShipmentRequest({
    requestPayload: manualShipmentsData ? manualShipmentsData : updatedShipmentRequest,
    enabled: enableCreateShipmentApiCall,
  });

  useEffect(() => {
    const allValidated = validatingItemsData.every((order) => order.quantity === 0);
    setAllItemsValidated(allValidated);
  }, [validatingItemsData]);

  useEffect(() => {
    setIsCustomTrackingAllowed(UPDATE_TRACKING_ALLOWED_IDS.includes(validatingItems?.selectedQuote?.shipmentItems[0]?.productId));
  }, [validatingItems]);

  useEffect(() => {
    if (validationSearchText) {
      handleSearch();
    }
  }, []);

  const handleSearch = () => {
    let barcodeFound = false;

    setValidatingItemsData((prevData) =>
      prevData?.map((order) => {
        if (!barcodeFound && order.barcode === validationSearchText && order.quantity > 0) {
          barcodeFound = true;
          return {
            ...order,
            validatedQuantity: order.validatedQuantity + 1,
            quantity: order.quantity - 1,
          };
        }
        return order;
      })
    );
    setValidationSearchText("");
  };

  const handleToggleSwitch = (id) => {
    setValidatingItemsData((prevData) =>
      prevData?.map((order, index) => {
        if (index === id) {
          const validatedQuantity = order.validatedQuantity + order.quantity;
          return { ...order, validatedQuantity, quantity: 0, isFullyValidated: true, skuManuallyChecked: true };
        }
        return order;
      })
    );
  };

  const [actionUpdateRequest, setActionUpdateRequest] = useState({ requestAvailable: false, request: [] });
  const { successfullUpdatedAction } = useUpdateAction({
    updateActionRequest: actionUpdateRequest.request,
    enabled: actionUpdateRequest.requestAvailable,
  });

  useEffect(() => {
    if (successfullUpdatedAction === true) {
      onValidation();
    }
  }, [successfullUpdatedAction]);

  useEffect(() => {
    if (allItemsValidated) {
      if (validatingItemsData[0].shipmentDetails && validatingItemsData[0].shipmentDetails?.length > 0 && !manualShipmentsData) {
        setEnablePrintProcedure(true);
      } else if (labelNumberFLag) {
        setEnableCreateShipmentApiCall(true);
      } else if (
        (validatingItems?.selectedQuote &&
          !UPDATE_TRACKING_ALLOWED_IDS.includes(validatingItems?.selectedQuote?.shipmentItems[0]?.productId)) ||
        manualShipmentsData
      ) {
        setEnableCreateShipmentApiCall(true);
      }
    }
  }, [allItemsValidated, labelNumberFLag]);

  useEffect(() => {
    if (enablePrintProcedure) {
      validatingItemsData[0].shipmentDetails?.map((shipmentDetail) =>
        usePrinter(shipmentDetail.pdfLink, shipmentDetail.courierName)
      );
      const actionUpdateRequest = prepareActionUpdateRequest(validatingItemsData);
      setActionUpdateRequest({ requestAvailable: true, request: [actionUpdateRequest] });
    } else if (createShipmentRequestResponse?.shipment) {
      const shippingCarrier =
        manualShipmentsData?.shippingCarrier == null
          ? validatingItems?.selectedQuote?.shippingCarrier
          : manualShipmentsData?.shippingCarrier;
      usePrinter(createShipmentRequestResponse?.shipment?.shipmentLabelUrl, shippingCarrier);
      const actionUpdateRequest = prepareActionUpdateRequest(validatingItemsData);
      setActionUpdateRequest({ requestAvailable: true, request: [actionUpdateRequest] });
    }
  }, [enablePrintProcedure, createShipmentRequestResponse?.shipment]);

  useEffect(() => {
    if (createShipmentRequestResponse?.code === "500") {
      onError();
    }
  }, [createShipmentRequestResponse?.code]);

  const prepareActionUpdateRequest = (items) => {
    const actionUpdateRequest = items?.map((i) => {
      return {
        sku: i.sku,
        allQtsScanned: i.skuManuallyChecked ? false : true,
        skuManuallyChecked: i.skuManuallyChecked || false,
      };
    });

    return {
      itemValidatedList: actionUpdateRequest,
      actionType: "PRINTED",
      idList: [validatingItems._id],
      changes: [{ fieldName: "shippingStatus", oldValue: "", newValue: "PickAndPack" }],
    };
  };

  const CustomSwitch = styled((props: SwitchProps) => <Switch {...props} />)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: globalTheme.blue,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: globalTheme.blue,
    },
  }));

  return (
    <DialogContent>
      <Stack spacing={3}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          style={{ width: "100%" }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={3}>
            <TextField
              id="standard-helperText"
              label="Search order by barcode"
              variant="standard"
              value={validationSearchText}
              onChange={(e) => setValidationSearchText(e.target.value)}
              fullWidth
              autoFocus
              sx={{ marginRight: "24px" }}
            />

            <Button variant="contained" onClick={handleSearch} sx={{ width: "275px" }} disabled={allItemsValidated}>
              Search
            </Button>
          </Stack>
        </form>
        {validatingItemsData.map((order, id) => (
          <Stack direction="row" justifyContent="space-between" key={id}>
            <Stack direction="row" spacing={2} alignItems="center" margin={0.5}>
              <a href={order?.imageURL} target="_blank" rel="noopener noreferrer">
                <img src={order?.imageURL} width={"75px"} height="75px" alt="product" />
              </a>
              <Stack>
                <Typography variant="subtitle1">{order?.title}</Typography>

                <Typography variant="caption" color="primary">
                  {order?.varyingAttributes}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption">SKU:</Typography>
                  <Typography variant="caption" color="primary">
                    {order?.sku}
                  </Typography>

                  <Typography variant="caption">Validated:</Typography>
                  <Typography variant="h6">{order?.validatedQuantity}</Typography>
                  <Typography variant="caption">out of</Typography>
                  <Typography variant="h6">{order?.quantity + order?.validatedQuantity}</Typography>
                </Stack>
              </Stack>
            </Stack>

            <Box width="120px">
              <FormControlLabel
                control={<CustomSwitch checked={order.isFullyValidated} onChange={() => handleToggleSwitch(id)} />}
                label={
                  <Typography variant="caption" color={globalTheme.blue}>
                    Validated manually
                  </Typography>
                }
              />
            </Box>
          </Stack>
        ))}
        {isCustomTrackingAllowed && (
          <InputBase
            sx={{
              ml: 1,
              flex: 1,
              fontSize: "14px",
              backgroundColor: globalTheme.background,
            }}
            placeholder="Update tracking number"
            inputProps={{ "aria-label": "Update tracking number" }}
            size="small"
            value={newLabelNumber}
            onChange={(e) => {
              setNewLabelNumber(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (newLabelNumber !== "") {
                  setLabelNumberFLag(true);
                }
              }
            }}
          />
        )}
      </Stack>
    </DialogContent>
  );
};
